// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { ClaimOrderLineItemAttributes } from './../store/orderCreation';
import { getUserTierConfig } from './../lib/userTierConfig';
import { createTraceEvent } from './../lib/ApplicationInsights/customTelemetryCreators';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

const defaultToDatabaseShipToVirtualSkuList = ['ebp', 'ebs'];
const inStoreExchangeVirtualSkuList = ['mse'];
const isAdvancedExchangeVirtualSkuList = ['sad', 'ebp', 'ebs'];
const walkInCenterVirtualSkuList = ['VMF'];

function isAdvancedExchangeVirtualSku(virtualSku: string) {
    return isAdvancedExchangeVirtualSkuList.some(
        skuToCheck => skuToCheck.toUpperCase() === virtualSku.toUpperCase()
    );
}

function shouldDefaultToDatabaseShipToVirtualSku(virtualSku: string) {
    return defaultToDatabaseShipToVirtualSkuList.some(
        skuToCheck => skuToCheck.toUpperCase() === virtualSku.toUpperCase()
    );
}

function isInStoreExchangeVirtualSku(virtualSku: string) {
    return inStoreExchangeVirtualSkuList.some(
        skuToCheck => skuToCheck.toUpperCase() === virtualSku.toUpperCase()
    );
}

export function checkOffersForShippingOffer(shippingOffers: any[]) {
    const foundShippingOffer = shippingOffers.some(
        (shippingOffer: any) =>
            Array.isArray(shippingOffer.offers) &&
            shippingOffer.offers.some(
                (item: any) =>
                    typeof item.virtualSku === 'string' &&
                    // TODO: Fix bad logic - we should only care about one or the other, if it is not mse then it doesn't matter if it is any of the others
                    (!isInStoreExchangeVirtualSku(item.virtualSku) ||
                        isAdvancedExchangeVirtualSku(item.virtualSku))
            )
    );
    return foundShippingOffer;
}

export function defaultToDatabaseShipToAddress(
    shippingOffers: any[],
    selectedOffers: any[]
) {
    const defaultToShipToAddress =
        getUserTierConfig().defaultToStoreAddress &&
        Array.isArray(shippingOffers) &&
        shippingOffers.some(
            (shippingOffer: any) =>
                Array.isArray(shippingOffer.offers) &&
                shippingOffer.offers.some(
                    (item: any) =>
                        typeof item.virtualSku === 'string' &&
                        shouldDefaultToDatabaseShipToVirtualSku(item.virtualSku)
                )
        );
    return defaultToShipToAddress;
}

export function isShippingAddressRequired(
    shippingOffers: any[],
    selectedOffers: any[]
) {
    const oldFoundShippingOffer =
        Array.isArray(shippingOffers) &&
        shippingOffers.some(
            (shippingOffer: any) =>
            {
                const selectedOffer = selectedOffers[shippingOffer.symptomCode];
                return (
                    (
                        selectedOffer &&
                        selectedOffer.offerAttributes !== ClaimOrderLineItemAttributes.InStore &&
                        selectedOffer.offerAttributes !== ClaimOrderLineItemAttributes.InStorePart
                    ) ||
                    (
                        Array.isArray(shippingOffer.offers) &&
                        shippingOffer.offers.some((item: any) => {
                            return (
                                typeof item.virtualSku === 'string' &&
                                isAdvancedExchangeVirtualSku(item.virtualSku)
                            );
                        })
                    )
                );
            }
        );
    const foundShippingOffer =
        Array.isArray(shippingOffers) &&
        shippingOffers.some(
            (shippingOffer: any) =>
                Array.isArray(shippingOffer.offers) &&
                shippingOffer.offers.some(
                    (offer: any) => offer.isShippingAddressRequired
                )
        );
    if (oldFoundShippingOffer !== foundShippingOffer) {
        // Adding this telemetry for now because this is such a critical area.
        // If this event gets fired we should understand it it.
        // In the long term, once we have some time under the new code, we should remove the old logic and this warning.
        createTraceEvent(
            `Found shipping discrepancy. Old UI shipping logic returned ${oldFoundShippingOffer}, \
            whereas new service isShippingAddressRequired returned ${foundShippingOffer}`,
            { shippingOffers, selectedOffers },
            SeverityLevel.Warning
        );
    }
    return foundShippingOffer;
}

export function isInStoreExchange(
    shippingOffers: any[],
    selectedOffers: any[]
) {
    const foundInStoreExchange =
        Array.isArray(shippingOffers) &&
        shippingOffers.some(
            (shippingOffer: any) =>
                selectedOffers[shippingOffer.symptomCode] &&
                (selectedOffers[shippingOffer.symptomCode].offerAttributes ===
                    ClaimOrderLineItemAttributes.InStore ||
                    selectedOffers[shippingOffer.symptomCode]
                        .offerAttributes ===
                        ClaimOrderLineItemAttributes.InStorePart ||
                    selectedOffers[shippingOffer.symptomCode]
                        .offerAttributes ===
                        ClaimOrderLineItemAttributes.Repair) &&
                Array.isArray(shippingOffer.offers) &&
                shippingOffer.offers.some(
                    (item: any) =>
                        typeof item.virtualSku === 'string' &&
                        (isInStoreExchangeVirtualSku(item.virtualSku) ||
                            isAdvancedExchangeVirtualSku(item.virtualSku))
                )
        );
    return foundInStoreExchange;
}

export function isWalkInCenter(virtualSku: string): boolean {
    return walkInCenterVirtualSkuList.includes(
        (virtualSku || '').toUpperCase()
    );
}

export function containsWalkInCenterOffer(shippingOffers: any[]) {
    const containsWicOffer: boolean = shippingOffers.some(
        (shippingOffer: any) =>
            Array.isArray(shippingOffer.offers) &&
            shippingOffer.offers.some(
                (item: any) =>
                    typeof item.virtualSku === 'string' &&
                    isWalkInCenter(item.virtualSku)
            )
    );
    return containsWicOffer;
}

export function defaultToWalkCenterAddressSelected(
    shippingOffer: any,
    wicSelectedAddress: any
) {
    return (
        containsWalkInCenterOffer(shippingOffer) &&
        wicSelectedAddress !== undefined
    );
}

export function isWalkInCenterSelected(selectedShippingOffers: any) {
    const offers = Object.values(selectedShippingOffers);

    return (
        offers.length === 1 &&
        offers.some((offer: any) => isWalkInCenter(offer.virtualSku))
    );
}

export function walkCenterIsMixedWithOther(selectedShippingOffers: any) {
    const offers = Object.values(selectedShippingOffers);

    const isWicSelected = offers.some((offer: any) =>
        isWalkInCenter(offer.virtualSku)
    );

    return (
        isWicSelected &&
        offers.some((offer: any) => !isWalkInCenter(offer.virtualSku))
    );
}

export function isWalkInCenterSelectedForMoreOffers(selectedShippingOffers: any) {
    const offers = Object.values(selectedShippingOffers);

    return (
        offers.some((offer: any) => isWalkInCenter(offer.virtualSku))
    );
}